import { LOGIN_PATH } from '@/common/main/config';
import { TokenVerificationError } from '../../domain/errors/AuthErrors';
import authServices from '../DependencyInjection';
import { handleAuthError } from '../../infrastructure/utils/authUtils';
import { absorbEmbedAccessToken } from '../../application/absorbEmbedAccessToken';
import { absorbErpExchangeAssertion } from '../../application/absorbErpExchangeAssertion';
import { notifyEmbedTokenReady } from '../../application/embedAuthEvents';
import { forceEmbedFromErpLanding, syncEmbeddedModeFromSearch, withEmbedQueryIfNeeded } from '@/common/application/embeddedMode';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { organizationTimeFormatService } from '@/common/infrastructure/services/OrganizationTimeFormatService';
import { organizationTimezoneService } from '@/common/infrastructure/services/OrganizationTimezoneService';

const { tokenService, authService } = authServices;

/** RWT-7864: provision legend/messaging is public (ERP WebView, no Raptor login). */
function isPublicProvisionPath(pathname: string): boolean {
  return /\/user-management\/provision\/?$/i.test(pathname);
}

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const didRunRef = useRef(false);
  const lastPathRef = useRef(location.pathname);
  const isPublicProvision = isPublicProvisionPath(location.pathname);

  useEffect(() => {
    // Sticky ERP embed for chrome-hide (TROC-317) before any auth redirect.
    // ERP landing may omit ?embed=1; Authorization or tenant query still means WebView.
    forceEmbedFromErpLanding(location.search);
    syncEmbeddedModeFromSearch(location.search);

    if (isPublicProvision) {
      absorbErpExchangeAssertion({
        search: location.search,
        replaceSearch: (nextSearch) => {
          const url = `${location.pathname}${nextSearch}${location.hash}`;
          window.history.replaceState(null, '', url);
        }
      });
    }

    // Leaving public provision → protected UM: re-run auth (token may only exist in storage now).
    if (lastPathRef.current !== location.pathname) {
      const leftPublicProvision = isPublicProvisionPath(lastPathRef.current) && !isPublicProvisionPath(location.pathname);
      if (leftPublicProvision) {
        didRunRef.current = false;
      }
      lastPathRef.current = location.pathname;
    }

    if (isPublicProvision) {
      // Do not lock didRunRef on public routes — otherwise provision→users never re-auths.
      return;
    }

    if (didRunRef.current) return;
    didRunRef.current = true;

    const redirectToLogin = () => {
      tokenService.removeToken();
      handleAuthError(authService);
      navigate(withEmbedQueryIfNeeded(LOGIN_PATH), { replace: true });
    };

    try {
      absorbEmbedAccessToken({
        search: location.search,
        getToken: () => tokenService.getToken(),
        setToken: (token) => tokenService.setToken(token),
        verifyToken: (token) => {
          try {
            return tokenService.verifyToken(token);
          } catch {
            return false;
          }
        },
        replaceSearch: (nextSearch) => {
          const url = `${location.pathname}${nextSearch}${location.hash}`;
          window.history.replaceState(null, '', url);
        }
      });

      const token = tokenService.getToken() || '';
      if (!tokenService.verifyToken(token)) {
        redirectToLogin();
        return;
      }

      notifyEmbedTokenReady();

      const shouldFetchTimezone = () => {
        const userManuallySelected = localStorage.getItem('userTimezoneManuallySelected') === 'true';
        if (userManuallySelected) {
          return false;
        }
        const existingTimezone = organizationTimezoneService.getOrganizationDefaultTimezone();
        return !existingTimezone;
      };

      if (shouldFetchTimezone()) {
        void organizationTimezoneService
          .fetchOrganizationDefaultTimezoneWithFallback()
          .then((orgTimezone) => {
            organizationTimezoneService.storeOrganizationDefaultTimezone(orgTimezone.timezone);
          })
          .catch(() => {
            // Silently fail - timezone fetch is non-critical for auth flow
          });
      }

      const shouldFetchTimeFormat = () => {
        const existingTimeFormat = organizationTimeFormatService.getOrganizationDefaultTimeFormat();
        return !existingTimeFormat;
      };

      if (shouldFetchTimeFormat()) {
        void organizationTimeFormatService
          .fetchOrganizationDefaultTimeFormatWithFallback()
          .then((orgTimeFormat) => {
            organizationTimeFormatService.storeOrganizationDefaultTimeFormat(orgTimeFormat.timeFormat);
          })
          .catch(() => {
            // Silently fail - time format fetch is non-critical for auth flow
          });
      }
    } catch (error) {
      if (error instanceof TokenVerificationError) {
        redirectToLogin();
      }
    }
  }, [navigate, isPublicProvision, location.pathname, location.search, location.hash]);

  return children;
};

export default AuthGuard;
